import { useEffect, useMemo, useState } from 'react';
import LoadingState from '../components/common/LoadingState';
import {
  listAuthorCreators,
  STAFF_PROFILE_CATEGORIES,
} from '../services/authorCreatorService';

const TEAM_SECTIONS = [
  { category: 'Education Team', heading: 'Education Team' },
  { category: 'Content Writer', heading: 'Content Writer' },
  { category: 'Support Staff', heading: 'Support Staff' },
];

function getStaffCategory(profile) {
  if (STAFF_PROFILE_CATEGORIES.includes(profile?.category)) {
    return profile.category;
  }

  return 'Education Team';
}

export default function AboutPage() {
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isSubscribed = true;

    async function loadStaffProfiles() {
      setLoading(true);
      setErrorMessage('');

      try {
        const result = await listAuthorCreators();
        if (!isSubscribed) {
          return;
        }

        setStaffProfiles(result);
      } catch (error) {
        if (!isSubscribed) {
          return;
        }

        setErrorMessage(error.message ?? 'Failed to load staff profiles.');
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    }

    loadStaffProfiles();

    return () => {
      isSubscribed = false;
    };
  }, []);

  const profilesByCategory = useMemo(() => {
    const grouped = {
      'Content Writer': [],
      'Education Team': [],
      'Support Staff': [],
    };

    for (const profile of staffProfiles) {
      const category = getStaffCategory(profile);
      grouped[category].push(profile);
    }

    return grouped;
  }, [staffProfiles]);

  return (
    <div className="page-stack">
      <section className="section-heading">
        <div>
          <p className="eyebrow">About</p>
          <h1>About Disinformation Warriors</h1>
          <p>
            Disinformation Warriors is a creative study focused on media literacy,
            critical thinking, and community-driven digital citizenship.
          </p>
        </div>
      </section>

      <section className="gradient_panel_text">
        <div className="panel_text">
          <h3>Origin Story</h3>
          <p>
            Started in the summer of 2020 by artists, writers, and concerned
            citizens, Disinformation Warriors investigates whether research,
            education, and animation can help communities push back against online
            disinformation.
          </p>
          <p>
            A project of{' '}
            <a
              className="navbar-link"
              href="http://animatedobjects.ca/"
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: 'var(--lime-green)' }}
            >
              Calgary Animated Objects Society (CAOS)
            </a>
            , supported by the{' '}
            <a
              className="navbar-link"
              href="https://www.canada.ca/en/canadian-heritage/services/online-disinformation.html"
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: 'var(--lime-green)' }}
            >
              Digital Citizen Initiative
            </a>{' '}
            and funded by the{' '}
            <a
              className="navbar-link"
              href="https://www.canada.ca/en/canadian-heritage.html"
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: 'var(--lime-green)' }}
            >
              Department of Canadian Heritage
            </a>
            .
          </p>
        </div>
      </section>

      <section className="gradient_panel_text">
        <div className="panel_text">
          <h3>Land Acknowledgement</h3>
          <p>
            We honour and acknowledge Moh&apos;kinsstis (Calgary), Treaty 7
            territory, the Blackfoot Confederacy (Siksika, Kainai, Piikani), the
            Iyaxe Nakoda and Tsuut&apos;ina Nations, and the Metis Nation of
            Alberta Region 3. We acknowledge all Nations, Indigenous and non,
            who live, work, play, and create on this land.
          </p>
        </div>
      </section>

      {loading ? <LoadingState label="Loading staff profiles..." /> : null}
      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}

      {!loading
        ? TEAM_SECTIONS.map((section) => {
            const profiles = profilesByCategory[section.category] || [];
            return (
              <section key={section.category} className="gradient_panel_text">
                <div className="panel_text">
                  <h2 className="project_team_title">{section.heading}</h2>
                  {profiles.length === 0 ? (
                    <p>No staff available.</p>
                  ) : (
                    <div className="staff-grid">
                      {profiles.map((profile) => (
                        <article key={profile.id} className="staff-card">
                          {profile.headshotUrl ? (
                            <img
                              className="staff-card-image"
                              src={profile.headshotUrl}
                              alt={profile.name || 'Staff profile'}
                            />
                          ) : null}

                          <h3>{profile.name || 'No name'}</h3>
                          <p>{profile.projectTitleRole || profile.profile || 'No role listed.'}</p>

                          {profile.bio ? <p>{profile.bio}</p> : null}
                          {profile.website ? (
                            <a href={profile.website} target="_blank" rel="noreferrer noopener">
                              {profile.website}
                            </a>
                          ) : null}
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            );
          })
        : null}

    </div>
  );
}
